import { useState, useEffect } from 'react';
import { fetchLeaderboard } from '../utils/api';
import { calculateAuraStats, getAuraBadge } from '../utils/aura';

const CATEGORIES = [
  { id: 'overall', name: 'Overall', emoji: '🏆', icon: '/overallicon.png' },
  // Good categories
  { id: 'drip_king', name: 'Drip King', emoji: '👑', icon: '/dripkingicon.png' },
  { id: 'drip_queen', name: 'Drip Queen', emoji: '👸', icon: '/dripqueenicon.png' },
  { id: 'bussin', name: "Bussin'", emoji: '✨', icon: '/bussinicon.png' },
  { id: 'fire_fit', name: 'Fire Fit', emoji: '🔥', icon: '/firefiticon.png' },
  // Bad categories
  { id: 'mid', name: 'Mid', emoji: '😐', icon: '/midicon.png' },
  { id: 'ohio', name: 'Ohio', emoji: '🌽', icon: '/ohioicon.png' },
  { id: 'cringe', name: 'Cringe', emoji: '😬', icon: '/cringeicon.png' },
  { id: 'cooked', name: 'Cooked', emoji: '💀', icon: '/cookedicon.png' },
];

function Leaderboard({ onNavigate }) {
  const [leaderboard, setLeaderboard] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('overall');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadLeaderboard();
  }, [selectedCategory]);

  const loadLeaderboard = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await fetchLeaderboard(selectedCategory);
      setLeaderboard(data);
    } catch (err) {
      setError('Failed to load leaderboard');
      console.error('Leaderboard error:', err);
    } finally {
      setLoading(false);
    }
  };

  const getMedalEmoji = (rank) => {
    return `#${rank}`;
  };

  return (
    <div className="leaderboard-container">
      <div className="leaderboard-header">
        <h1 className="leaderboard-title">🏆 Leaderboard 🏆</h1>
        <p className="leaderboard-subtitle">See who's got the most drip</p>
      </div>

      {/* Category Selector */}
      <div className="category-selector">
        <h2 className="selector-title">Category:</h2>
        <div className="category-buttons">
          {CATEGORIES.map(category => (
            <button
              key={category.id}
              className={`category-button ${selectedCategory === category.id ? 'active' : ''}`}
              onClick={() => setSelectedCategory(category.id)}
            >
              {category.icon ? (
                <img src={category.icon} alt={category.name} className="category-icon" />
              ) : (
                category.emoji
              )} {category.name}
            </button>
          ))}
        </div>
      </div>

      {/* Leaderboard List */}
      {loading ? (
        <div className="loading-container">
          <div className="spinner large"></div>
          <p>Loading leaderboard...</p>
        </div>
      ) : error ? (
        <div className="error-container">
          <p className="error-text">{error}</p>
          <button onClick={loadLeaderboard} className="retry-button">
            Retry
          </button>
        </div>
      ) : leaderboard.length === 0 ? (
        <div className="empty-state">
          <p>No entries yet. Be the first!</p>
        </div>
      ) : (
        <div className="leaderboard-list">
          {/* Top 3 Podium */}
          {leaderboard.length >= 3 && (
            <div className="podium-container">
              {/* 2nd Place - Left */}
              <div className="podium-spot second-place" onClick={() => onNavigate && onNavigate('profile', leaderboard[1].username)} style={{ cursor: 'pointer' }}>
                <div className="podium-avatar-wrapper">
                  <img
                    src={leaderboard[1].avatarUrl}
                    alt={leaderboard[1].username}
                    className="podium-avatar"
                  />
                  {(() => {
                    const auraStats = calculateAuraStats(
                      leaderboard[1].auraStats?.fireSwipes || 0,
                      leaderboard[1].auraStats?.leftSwipes || 0
                    );
                    const auraBadge = getAuraBadge(auraStats.auraPercent, auraStats.totalSwipes);
                    return (
                      <div className="aura-badge-overlay">
                        <img src={auraBadge.emoji} alt={auraBadge.label} className="aura-badge-image" />
                      </div>
                    );
                  })()}
                </div>
                <div className="podium-info">
                  <div className="podium-crown">
                    {(() => {
                      const category = CATEGORIES.find(c => c.id === selectedCategory);
                      return category?.icon ? (
                        <img src={category.icon} alt={category.name} className="category-icon" />
                      ) : (
                        category?.emoji || '🥈'
                      );
                    })()}
                  </div>
                  <div className="podium-username">{leaderboard[1].username}</div>
                  <div className="podium-votes">{leaderboard[1].votes} votes</div>
                </div>
                <div className="podium-base silver-base">
                  <div className="base-label">2nd Place</div>
                </div>
              </div>

              {/* 1st Place - Center */}
              <div className="podium-spot first-place" onClick={() => onNavigate && onNavigate('profile', leaderboard[0].username)} style={{ cursor: 'pointer' }}>
                <div className="podium-avatar-wrapper">
                  <img
                    src={leaderboard[0].avatarUrl}
                    alt={leaderboard[0].username}
                    className="podium-avatar"
                  />
                  {(() => {
                    const auraStats = calculateAuraStats(
                      leaderboard[0].auraStats?.fireSwipes || 0,
                      leaderboard[0].auraStats?.leftSwipes || 0
                    );
                    const auraBadge = getAuraBadge(auraStats.auraPercent, auraStats.totalSwipes);
                    return (
                      <div className="aura-badge-overlay">
                        <img src={auraBadge.emoji} alt={auraBadge.label} className="aura-badge-image" />
                      </div>
                    );
                  })()}
                </div>
                <div className="podium-info">
                  <div className="podium-crown champion">
                    {(() => {
                      const category = CATEGORIES.find(c => c.id === selectedCategory);
                      return category?.icon ? (
                        <img src={category.icon} alt={category.name} className="category-icon" />
                      ) : (
                        category?.emoji || '👑'
                      );
                    })()}
                  </div>
                  <div className="podium-username champion">{leaderboard[0].username}</div>
                  <div className="podium-votes">{leaderboard[0].votes} votes</div>
                </div>
                <div className="podium-base gold-base">
                  <div className="base-label">{CATEGORIES.find(c => c.id === selectedCategory)?.name || 'Overall'}</div>
                </div>
              </div>

              {/* 3rd Place - Right */}
              <div className="podium-spot third-place" onClick={() => onNavigate && onNavigate('profile', leaderboard[2].username)} style={{ cursor: 'pointer' }}>
                <div className="podium-avatar-wrapper">
                  <img
                    src={leaderboard[2].avatarUrl}
                    alt={leaderboard[2].username}
                    className="podium-avatar"
                  />
                  {(() => {
                    const auraStats = calculateAuraStats(
                      leaderboard[2].auraStats?.fireSwipes || 0,
                      leaderboard[2].auraStats?.leftSwipes || 0
                    );
                    const auraBadge = getAuraBadge(auraStats.auraPercent, auraStats.totalSwipes);
                    return (
                      <div className="aura-badge-overlay">
                        <img src={auraBadge.emoji} alt={auraBadge.label} className="aura-badge-image" />
                      </div>
                    );
                  })()}
                </div>
                <div className="podium-info">
                  <div className="podium-crown">
                    {(() => {
                      const category = CATEGORIES.find(c => c.id === selectedCategory);
                      return category?.icon ? (
                        <img src={category.icon} alt={category.name} className="category-icon" />
                      ) : (
                        category?.emoji || '🥉'
                      );
                    })()}
                  </div>
                  <div className="podium-username">{leaderboard[2].username}</div>
                  <div className="podium-votes">{leaderboard[2].votes} votes</div>
                </div>
                <div className="podium-base bronze-base">
                  <div className="base-label">3rd Place</div>
                </div>
              </div>
            </div>
          )}

          {/* Rest of Rankings */}
          <div className="rankings-list">
            {leaderboard.map((entry, index) => {
              const auraStats = calculateAuraStats(
                entry.auraStats?.fireSwipes || 0,
                entry.auraStats?.leftSwipes || 0
              );
              const auraBadge = getAuraBadge(auraStats.auraPercent, auraStats.totalSwipes);

              return (
                <div
                  key={entry.id}
                  className={`ranking-item ${index < 3 ? 'top-three' : ''}`}
                  onClick={() => onNavigate && onNavigate('profile', entry.username)}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="ranking-position">
                    {getMedalEmoji(index + 1)}
                  </div>
                  <div className="ranking-avatar-container">
                    <img
                      src={entry.avatarUrl}
                      alt={entry.username}
                      className="ranking-avatar"
                    />
                    <div className="aura-badge-overlay-small">
                      <img src={auraBadge.emoji} alt={auraBadge.label} className="aura-badge-image-small" />
                    </div>
                  </div>
                  <div className="ranking-info">
                    <p className="ranking-username">{entry.username}</p>
                    <p className="ranking-votes">{entry.votes} votes</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

export default Leaderboard;
